import { debounce } from '@theme/coco-utilities';

const SECTION_ID = 'coco-search-results';
const CE_NAME = 'coco-search';
const MIN_LOADING_MS = 300;
const suggestionCache = new Map();

/**
 * Search field controls (clear / submit / loader) + optional live suggestions.
 * Custom element: <form is="coco-search">
 * Predictive mode: add data-predictive (+ results panel markup).
 */
class CocoSearchForm extends HTMLFormElement {
  #query = '';
  #abort = new AbortController();
  #loadingStartedAt = 0;
  #loadingTimer = null;
  #bound = false;
  #panel = null;
  #statusEl = null;
  #loadingLabel = '';

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.#panel = this.querySelector('[data-coco-search-panel]');
    this.#wireEvents();
    this.#syncClearControl();

    this.#query = this.#readQuery();
    if (this.#predictive && this.#query) {
      this.toggleAttribute('results', true);
      this.#requestSuggestions(this.#query);
    }
  }

  get #predictive() {
    return this.hasAttribute('data-predictive');
  }

  get #field() {
    return this.querySelector('input[type="search"]');
  }

  get #clearBtn() {
    return this.querySelector('button[type="reset"]');
  }

  get #submitBtn() {
    return this.querySelector('button[type="submit"]');
  }

  get #spinner() {
    return this.#submitBtn?.querySelector('[data-loading-spinner]');
  }

  #wireEvents() {
    if (!this.#field) return;

    this.addEventListener('reset', (event) => this.#onReset(event));
    this.addEventListener('submit', (event) => this.#onSubmit(event));
    this.#field.addEventListener('input', () => this.#onType());
    this.#field.addEventListener('input', debounce(() => this.#onDebouncedType(), 300));
    this.#field.addEventListener('focus', () => this.#onFocus());
    this.addEventListener('focusout', () => this.#onBlur());
    this.addEventListener('keyup', () => this.#onKeyUp());
    this.addEventListener('keydown', (event) => this.#onKeyDown(event));
  }

  #readQuery() {
    return this.#field?.value.trim() || '';
  }

  #syncClearControl() {
    const clearBtn = this.#clearBtn;
    const field = this.#field;
    if (!clearBtn || !field) return;

    const hasValue = field.value.length > 0;
    clearBtn.style.opacity = hasValue ? '1' : '0';
    clearBtn.style.pointerEvents = hasValue ? '' : 'none';
    clearBtn.tabIndex = hasValue ? 0 : -1;
    clearBtn.setAttribute('aria-hidden', hasValue ? 'false' : 'true');
    this.#submitBtn?.classList.toggle('hidden', hasValue);
  }

  #canClear() {
    return !this.querySelector('[aria-selected="true"] a');
  }

  #onType() {
    this.#syncClearControl();

    if (!this.#predictive) return;

    if (!this.#readQuery().length) {
      this.#dismiss(true);
      return;
    }

    this.#beginLoading();
  }

  #onDebouncedType() {
    if (!this.#predictive) return;

    const next = this.#readQuery();

    if (!this.#query || !next.startsWith(this.#query)) {
      this.querySelector('[data-coco-search-groups]')?.remove();
    }

    this.#rewriteSearchForLabel(this.#query, next);
    this.#query = next;

    if (!this.#query.length) return;

    this.#abort.abort();
    this.#abort = new AbortController();
    this.#requestSuggestions(this.#query);
  }

  #onSubmit(event) {
    if (this.#predictive) {
      if (!this.#readQuery().length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
      return;
    }

    // Search page: keep native submit, show loader while navigating.
    if (this.#readQuery().length) {
      this.#setSpinner(true);
    } else {
      event.preventDefault();
    }
  }

  #onReset(event) {
    event.preventDefault();

    if (!this.#canClear()) return;

    this.#field.value = '';
    this.#field.focus();
    this.#syncClearControl();
    this.#query = '';
    this.#abort.abort();
    this.#abort = new AbortController();
    if (this.#predictive) this.#resetPanel(true);
  }

  #onFocus() {
    if (!this.#predictive) return;

    const current = this.#readQuery();
    if (!current.length) return;

    if (this.#query !== current) {
      this.#onDebouncedType();
    } else if (this.hasAttribute('results')) {
      this.#show();
    } else {
      this.#requestSuggestions(this.#query);
    }
  }

  #onBlur() {
    if (!this.#predictive) return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.#dismiss();
    });
  }

  #onKeyUp() {
    if (!this.#predictive) return;
    if (!this.#readQuery().length) this.#dismiss(true);
  }

  #onKeyDown(event) {
    if (!this.#predictive) return;

    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
      this.#moveHighlight(event.code === 'ArrowUp' ? -1 : 1);
      return;
    }

    if (event.code === 'Enter' && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.#activateHighlight();
    }
  }

  #rewriteSearchForLabel(previous, next) {
    const label = this.querySelector('[data-coco-search-for]');
    const text = label?.innerText;
    if (!text || !previous) return;
    if (text.match(new RegExp(previous, 'g'))?.length > 1) return;
    label.innerText = text.replace(previous, next);
  }

  #moveHighlight(step) {
    if (!this.hasAttribute('results')) return;

    const selected = this.querySelector('[aria-selected="true"]');
    const options = Array.from(
      this.querySelectorAll('li[role="option"], [data-coco-search-panel] a[href]'),
    ).filter((el) => el.offsetParent !== null);

    if (!options.length) return;
    if (step < 0 && !selected) return;

    const currentIndex = options.indexOf(selected);
    let nextIndex = currentIndex;

    if (selected && step > 0) {
      nextIndex = currentIndex === options.length - 1 ? 0 : currentIndex + 1;
    } else if (step < 0) {
      nextIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
    } else if (!selected) {
      nextIndex = 0;
    }

    if (nextIndex === currentIndex) return;

    const next = options[nextIndex];
    const link = next.matches('a[href]') ? next : next.querySelector('a[href]');

    if (this.#statusEl) this.#statusEl.textContent = '';
    selected?.setAttribute('aria-selected', 'false');
    next.setAttribute('aria-selected', 'true');
    this.#field.setAttribute('aria-activedescendant', next.id || link?.id || '');
  }

  #activateHighlight() {
    this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')?.click();
  }

  #requestSuggestions(term) {
    const cacheKey = term.replace(/\s+/g, '-').toLowerCase();

    if (!this.hasAttribute('data-loading')) {
      this.#beginLoading();
    }

    if (suggestionCache.has(cacheKey)) {
      this.#waitThen(term, () => this.#paintResults(suggestionCache.get(cacheKey)));
      return;
    }

    const url = `${window.Coco.routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=${SECTION_ID}`;

    fetch(url, { signal: this.#abort.signal })
      .then((response) => {
        if (!response.ok) {
          this.#dismiss();
          throw new Error(String(response.status));
        }
        return response.text();
      })
      .then((html) => {
        const markup = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('#coco-search-results')?.innerHTML;

        if (!markup) {
          this.#endLoading();
          this.#dismiss();
          return;
        }

        suggestionCache.set(cacheKey, markup);
        this.#waitThen(term, () => this.#paintResults(markup));
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.#endLoading();
        this.#dismiss();
        throw error;
      });
  }

  #waitThen(term, callback) {
    const wait = Math.max(0, MIN_LOADING_MS - (Date.now() - this.#loadingStartedAt));
    clearTimeout(this.#loadingTimer);
    this.#loadingTimer = setTimeout(() => {
      if (this.#readQuery() !== term) return;
      callback();
      this.#endLoading();
    }, wait);
  }

  #beginLoading() {
    this.#loadingStartedAt = Date.now();
    this.#statusEl = this.#statusEl || this.querySelector('[data-coco-search-status]');
    this.#loadingLabel = this.#loadingLabel || this.getAttribute('data-loading-text') || '';

    if (this.#statusEl && this.#loadingLabel) {
      this.#announce(this.#loadingLabel);
    }

    this.setAttribute('data-loading', '');
    this.#setSpinner(true);
  }

  #announce(message) {
    if (!this.#statusEl || !message) return;
    this.#statusEl.setAttribute('aria-hidden', 'false');
    this.#statusEl.textContent = message;
    setTimeout(() => {
      this.#statusEl.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  #paintResults(markup) {
    if (!this.#panel || !markup) return;

    this.#panel.innerHTML = markup;
    this.toggleAttribute('results', true);

    const count = this.querySelector('[data-coco-search-count]');
    if (count) this.#announce(count.textContent);

    this.#show();
  }

  #endLoading() {
    clearTimeout(this.#loadingTimer);
    this.removeAttribute('data-loading');
    this.#setSpinner(false);
  }

  #show() {
    this.#field?.setAttribute('aria-expanded', 'true');
  }

  #dismiss(clearQuery = false) {
    this.#resetPanel(clearQuery);
  }

  #resetPanel(clearQuery = false) {
    clearTimeout(this.#loadingTimer);

    if (clearQuery) {
      this.#field.value = '';
      this.#query = '';
      this.#syncClearControl();
      this.removeAttribute('results');
      if (this.#panel) this.#panel.innerHTML = '';
    }

    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.#field?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('data-loading');
    this.#setSpinner(false);

    if (clearQuery) {
      this.#field?.setAttribute('aria-expanded', 'false');
    }
  }

  #setSpinner(active) {
    const submit = this.#submitBtn;
    if (!submit) return;

    const spinner = this.#spinner;

    if (active) {
      submit.classList.remove('hidden');
      this.#clearBtn?.style.setProperty('opacity', '0');
      this.#clearBtn?.style.setProperty('pointer-events', 'none');
      submit.setAttribute('aria-busy', 'true');

      Array.from(submit.children).forEach((child) => {
        child.classList.toggle('hidden', child !== spinner);
      });
      spinner?.classList.remove('hidden');
      return;
    }

    submit.removeAttribute('aria-busy');
    spinner?.classList.add('hidden');
    Array.from(submit.children).forEach((child) => {
      if (child !== spinner) child.classList.remove('hidden');
    });
    this.#syncClearControl();
  }
}

if (!customElements.get(CE_NAME)) {
  customElements.define(CE_NAME, CocoSearchForm, { extends: 'form' });
}
